import { trackEvent } from '@/utils/gtags';

import {
  type SolutionContentSelection,
  type SolutionId,
  type SolutionSectionView,
  trackSolutionAudioPlay,
  trackSolutionContentSelection,
  trackSolutionSectionView,
} from '../shared/solutionAnalytics';

/**
 * ### INDUSTRIAL_SOLUTION_ID
 *
 * `solution_id` every Listen AI Industrial landing-page event carries.
 */
export const INDUSTRIAL_SOLUTION_ID =
  'listen_ai_industrial' satisfies SolutionId;

/**
 * ### IndustrialCaseContentType
 *
 * What a visitor clicked on a customer-case surface.
 *
 * `ask_ai` is one of the AI services offered for further reading, `case_cta`
 * the inquiry action closing a case, `case_card` a card in the case list or in
 * the related cases of a detail page.
 */
export type IndustrialCaseContentType = 'ask_ai' | 'case_cta' | 'case_card';

/**
 * ### IndustrialCaseIdentity
 *
 * What identifies a case in analytics. Industry travels with every case event
 * so reports can compare interest per industry without joining anything.
 */
export type IndustrialCaseIdentity = {
  /**
   * ### slug
   *
   * URL segment of the case, used as its analytics identifier.
   */
  slug: string;

  /**
   * ### solution
   *
   * Which product the case belongs to, as stored on the document.
   */
  solution: string;

  /**
   * ### industry
   *
   * Industry the case was carried out in.
   */
  industry: string;
};

/**
 * Cases store the product as `industrial` or `safety`, while every other event
 * on the site reports the full product id. Reports would otherwise split one
 * product across two spellings, so the stored value is widened here.
 */
function toSolutionId(solution: string): SolutionId {
  return solution === 'safety' ? 'listen_ai_safety' : INDUSTRIAL_SOLUTION_ID;
}

/**
 * ### trackIndustrialSectionView
 *
 * Reports a section view on the Industrial page. Binds the shared tracker to
 * {@link INDUSTRIAL_SOLUTION_ID}; the parameters are otherwise unchanged.
 *
 * @param view Section identity and its order within the page
 * @see {@link trackSolutionSectionView} The event this reports
 */
export function trackIndustrialSectionView(view: SolutionSectionView): void {
  trackSolutionSectionView(INDUSTRIAL_SOLUTION_ID, view);
}

/**
 * ### trackIndustrialContentSelection
 *
 * Reports a content selection on the Industrial page. Binds the shared tracker
 * to {@link INDUSTRIAL_SOLUTION_ID}; the parameters are otherwise unchanged.
 *
 * @param selection Chosen item, its kind, and its section
 * @see {@link trackSolutionContentSelection} The event this reports
 */
export function trackIndustrialContentSelection(
  selection: SolutionContentSelection,
): void {
  trackSolutionContentSelection(INDUSTRIAL_SOLUTION_ID, selection);
}

/**
 * ### trackIndustrialCaseView
 *
 * Reports that a published customer case was opened.
 *
 * Sent once per rendered case rather than per route change, so a reader moving
 * between cases produces one event each.
 *
 * @param caseIdentity The case being read
 */
export function trackIndustrialCaseView({
  slug,
  solution,
  industry,
}: IndustrialCaseIdentity): void {
  trackEvent('view_item', {
    item_id: slug,
    content_type: 'industrial_case',
    solution_id: toSolutionId(solution),
    industry,
  });
}

/**
 * ### trackIndustrialCaseSelection
 *
 * Reports a click on a customer-case surface: an AI service, the closing
 * inquiry action, or a case card.
 *
 * @param selection What was clicked and which case it belongs to
 */
export function trackIndustrialCaseSelection({
  contentType,
  itemId,
  caseIdentity,
}: {
  contentType: IndustrialCaseContentType;
  /** The AI service name, or the slug of the case a card leads to */
  itemId: string;
  caseIdentity: IndustrialCaseIdentity;
}): void {
  trackEvent('select_content', {
    content_type: contentType,
    item_id: itemId,
    solution_id: toSolutionId(caseIdentity.solution),
    industry: caseIdentity.industry,
    source_item_id: caseIdentity.slug,
  });
}

/**
 * ### trackIndustrialAudioPlay
 *
 * Reports the first playback of a sample in the Industrial sound section,
 * whose `section_id` is fixed to `sound_samples`.
 *
 * @param sampleId Stable identifier of the sample that started playing
 * @see {@link trackSolutionAudioPlay} The event this reports
 */
export function trackIndustrialAudioPlay(sampleId: string): void {
  trackSolutionAudioPlay(INDUSTRIAL_SOLUTION_ID, {
    sampleId,
    sectionId: 'sound_samples',
  });
}
